import { Intent, Tab, Tabs } from '@blueprintjs/core';
import * as go from 'gojs';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import * as React from 'react';
import { alphaSort, isEqual } from 'vet-bones/utils';
import { useDidValueChange } from 'vet-bones/utils/hooks';
import { SelectItem } from 'vet-bones/utils/select';

import { ColorPicker } from 'src/components/ColorPicker';
import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import { ProjectGeoDataByIri } from 'src/constants/projectGeoData';
import {
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { ProjectDiagramAttributesTable } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTable';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { ProjectDiagramClassDrawerGeospatialTab } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerGeospatialInputTab';
import { ProjectDiagramClassDrawerRelationships } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationships';
import {
  ClassDrawerTab,
  ClassRelationshipItem,
} from 'src/containers/ProjectDiagram/components/classDrawer/types';
import { ProjectDiagramEditableIri } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableIri';
import { ProjectDiagramEditableTextArea } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableTextArea';
import { NodeCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useCreateModelConceptClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import { useGenerateNewIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useGenerateNewIri';
import { addAttributeItemToClass } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptAttributeForClass';
import { addRelationshipItemToClass } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptRelationshipForClass';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ProjectDiagramCreateEntityDrawer } from 'src/containers/ProjectDiagram/ProjectDiagramCreateEntityDrawer';
import { ModelConceptClassHierarchy } from 'src/containers/ProjectDiagram/types/data';
import { getColorIdForIri } from 'src/containers/ProjectDiagram/utils/colorIds';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { generateModelConceptIri } from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { setGeoDataForIri } from 'src/containers/ProjectDiagram/utils/geoData';
import { RelationshipWithHierarchyData } from 'src/containers/ProjectDiagram/utils/getAllInheritedRelationships';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { addModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/addModelConceptHierarchy';
import { rescaleDiagram } from 'src/containers/ProjectDiagram/utils/rescaleDiagram';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { projectSelector } from 'src/selectors/project';
import { projectClassIrisSelector } from 'src/selectors/projectClassIris';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectClassRelationshipsByClassSelector } from 'src/selectors/projectClassRelationships';
import { projectModelItemsSelector } from 'src/selectors/projectModelItems';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { getProjectTargetModel } from 'src/utils/getProjectTargetModel';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramCreateClassDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const generateNewIri = useGenerateNewIri();
  const { projectId } = useRouterParams();
  const [selectedTabId, setSelectedTabId] = React.useState(
    ClassDrawerTab.BASIC
  );

  const project = useReduxSelector(projectSelector(projectId));

  const { isOpen } = useReduxSelector((state) => state.ui.createClassDrawer);

  const allModelItems = useReduxSelector(
    projectModelItemsSelector(projectId),
    isEqual
  );

  const allClassItems: SelectItem[] = useReduxSelector(
    projectClassItemsSelector(projectId),
    isEqual
  );

  const existingClassIris = useReduxSelector(
    projectClassIrisSelector(projectId)
  );

  const relationshipsByClassIri = useReduxSelector(
    projectClassRelationshipsByClassSelector(projectId)
  );

  const [errorText, setErrorText] = React.useState({
    label: copy.components.projectDiagram.editableIri.validationLabelRequired,
    relationship: '',
  });
  const [iri, setIri] = React.useState('');
  const [colorId, setColorId] = React.useState(
    diagram?.model ? getColorIdForIri(diagram.model, iri) : ''
  );

  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );
  const [models, setModels] = React.useState<SelectItem[]>(
    allModelItems.filter((item) => item.value === targetModelId)
  );

  const didIsOpenChange = useDidValueChange(isOpen);
  React.useEffect(() => {
    if (didIsOpenChange && isOpen) {
      setModels(allModelItems.filter((item) => item.value === targetModelId));
      setColorId(diagram?.model ? getColorIdForIri(diagram.model, iri) : '');
      setSelectedTabId(ClassDrawerTab.BASIC);
    }
  }, [didIsOpenChange, isOpen, diagram, iri, allModelItems, targetModelId]);

  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');

  const [didEditLabel, setDidEditLabel] = React.useState(false);

  const [parents, setParents] = React.useState<SelectItem[]>([]);
  const [children, setChildren] = React.useState<SelectItem[]>([]);

  const [addedRelationshipsByIri, setAddedRelationshipsByIri] = React.useState<
    Record<string, ClassRelationshipItem>
  >({});

  const [attributes, setAttributes] = React.useState<AttributeTableItem[]>([]);

  const [geoData, setGeoData] = React.useState<ProjectGeoDataByIri>({});

  const availableModels = React.useMemo(() => {
    const modelIds = new Set(models.map((item) => item.value));

    return allModelItems.filter((item) => !modelIds.has(item.value));
  }, [allModelItems, models]);

  const hierarchySelectItems = React.useMemo(() => {
    const parentIris = new Set(parents.map((item) => item.value));
    const childIris = new Set(children.map((item) => item.value));

    return {
      availableParents: allClassItems.filter(
        (item) => !parentIris.has(item.value)
      ),
      availableChildren: allClassItems.filter(
        (item) => !childIris.has(item.value)
      ),
    };
  }, [allClassItems, children, parents]);

  const inheritedRelationshipsByIri = React.useMemo(() => {
    return parents.reduce<Record<string, RelationshipWithHierarchyData>>(
      (acc, { value: parentIri }) => {
        const parentRelationshipsByIri =
          relationshipsByClassIri[parentIri] || {};
        Object.values(parentRelationshipsByIri).forEach((relationship) => {
          const addedRelationship = acc[relationship.iri];
          if (
            !addedRelationship ||
            addedRelationship.depth > relationship.depth + 1
          ) {
            // the depth is for the parent, so we must add 1 here for the to-be-created class
            acc[relationship.iri] = {
              ...relationship,
              depth: relationship.depth + 1,
            };
          }
        });

        return acc;
      },
      {}
    );
  }, [parents, relationshipsByClassIri]);

  const addedAndInheritedRelationships = React.useMemo<
    Array<ClassRelationshipItem & { depth?: number }>
  >(() => {
    const inheritedClassRelationshipItemsByIri = Object.values(
      inheritedRelationshipsByIri
    ).reduce<Record<string, ClassRelationshipItem & { depth?: number }>>(
      (acc, relationship) => {
        const directions = [];
        if (parents.some((parent) => relationship.domains.has(parent.value))) {
          directions.push(RelationshipDirection.OUTGOING);
        }
        if (parents.some((parent) => relationship.ranges.has(parent.value))) {
          directions.push(RelationshipDirection.INCOMING);
        }
        acc[relationship.iri] = {
          text: relationship.label,
          value: relationship.iri,
          directions,
          depth: relationship.depth,
        };
        return acc;
      },
      {}
    );
    const combinedRelationshipItemsByIri: Record<
      string,
      ClassRelationshipItem & { depth?: number }
    > = {
      ...inheritedClassRelationshipItemsByIri,
      ...addedRelationshipsByIri,
    };
    return Object.values(combinedRelationshipItemsByIri);
  }, [addedRelationshipsByIri, inheritedRelationshipsByIri, parents]);

  const allNewIris = React.useMemo(() => {
    const iris = new Set<string>();
    if (iri) {
      iris.add(iri);
    }
    parents.forEach((parent) => iris.add(parent.value));
    children.forEach((child) => iris.add(child.value));
    Object.values(addedRelationshipsByIri).forEach((relationship) => {
      iris.add(relationship.value);
    });
    attributes.forEach((attribute) => {
      attribute.ranges.forEach((range) => iris.add(range.value));
    });
    return iris;
  }, [iri, parents, children, addedRelationshipsByIri, attributes]);

  const createNewClassFromQuery =
    useCreateModelConceptClassFromQuery(allNewIris);

  const handleUpdateIri = (iri: string, invalid: string) => {
    setIri(iri);
    setErrorText({ ...errorText, label: invalid });
  };
  const handleUpdateLabel = (label: string, invalid: string) => {
    setLabel(label);
    setDidEditLabel(true);
    setErrorText({ ...errorText, label: invalid });
    if (!label) {
      setIri('');
      return;
    }

    if (!project) {
      return;
    }

    const initialLabelIri = generateModelConceptIri(
      getProjectTargetModel(project),
      label.trim()
    );
    const labelIri = generateNewIri(initialLabelIri);
    setIri(labelIri);
  };

  const handleAddModel = (selected: SelectItem | null) => {
    if (!selected) {
      return;
    }
    setModels([...models, selected].sort(alphaSort((item) => item.text)));
  };
  const handleRemoveModel = (selected: SelectItem) => {
    setModels(models.filter((item) => item.value !== selected.value));
  };

  const handleAddParent = (selected: SelectItem | null) => {
    if (!selected || (selected.isNewlyCreated && !selected.text)) {
      return;
    }
    setParents([...parents, selected].sort(alphaSort((item) => item.text)));
  };
  const handleRemoveParent = (selected: SelectItem) => {
    setParents(parents.filter((item) => item.value !== selected.value));
  };

  const handleAddChild = (selected: SelectItem | null) => {
    if (!selected || (selected.isNewlyCreated && !selected.text)) {
      return;
    }
    setChildren([...children, selected].sort(alphaSort((item) => item.text)));
  };
  const handleRemoveChild = (selected: SelectItem) => {
    setChildren(children.filter((item) => item.value !== selected.value));
  };

  const handleAddRelationship = (relationship: ClassRelationshipItem) => {
    if (!relationship || (relationship.isNewlyCreated && !relationship.text)) {
      return;
    }
    setAddedRelationshipsByIri({
      ...addedRelationshipsByIri,
      [relationship.value]: relationship,
    });
  };

  const updateRelationshipError = (
    relationshipsByIri: Record<string, ClassRelationshipItem>
  ) => {
    const areThereDirectionlessRelationships = Object.values(
      relationshipsByIri
    ).some((rel) => !rel.directions.length);
    setErrorText({
      ...errorText,
      relationship: areThereDirectionlessRelationships
        ? copy.components.projectDiagram.classDrawer.relationshipsTable
            .missingDirection
        : '',
    });
  };

  const handleUnlinkRelationship = (relationship: ClassRelationshipItem) => {
    const updatedRelationshipsByIri = omit(addedRelationshipsByIri, [
      relationship.value,
    ]);
    setAddedRelationshipsByIri(updatedRelationshipsByIri);
    updateRelationshipError(updatedRelationshipsByIri);
  };

  const handleUpdateRelationship = (
    relationship: ClassRelationshipItem | null
  ) => {
    if (!relationship) {
      return;
    }

    const updatedRelationshipsByIri = {
      ...addedRelationshipsByIri,
      [relationship.value]: relationship,
    };
    setAddedRelationshipsByIri(updatedRelationshipsByIri);
    updateRelationshipError(updatedRelationshipsByIri);
  };

  const handleAddAttribute = (attribute: AttributeTableItem) => {
    if (!attribute || (attribute.isNewlyCreated && !attribute.text)) {
      return;
    }
    setAttributes(
      [...attributes, attribute].sort(alphaSort((item) => item.text))
    );
  };

  const handleRemoveAttribute = (attribute: AttributeTableItem) => {
    if (!attribute) {
      return;
    }

    const updatedGeoData = omitBy(
      geoData,
      (geoProperty) =>
        geoProperty &&
        geoProperty.isAttribute &&
        geoProperty.value === attribute.value
    );
    setGeoData(updatedGeoData);

    setAttributes(attributes.filter((item) => item.value !== attribute.value));
  };

  const handleCancel = () => {
    setErrorText({
      label: copy.components.projectDiagram.editableIri.validationLabelRequired,
      relationship: '',
    });
    setIri('');
    setLabel('');
    setDescription('');
    setModels([]);
    setDidEditLabel(false);
    setParents([]);
    setChildren([]);
    setAddedRelationshipsByIri({});
    setAttributes([]);
    setGeoData({});
    setSelectedTabId(ClassDrawerTab.BASIC);
    dispatch(
      uiSlice.actions.toggledCreateClassDrawer({
        isOpen: false,
      })
    );
  };

  const handleSubmit = () => {
    if (
      errorText.label ||
      errorText.relationship ||
      !diagram ||
      !label.trim()
    ) {
      return;
    }

    commitDiagramModel(
      diagram,
      (model) => {
        const projectModelIds = models.map(({ value }) => value);

        addModelConceptClass({
          iri,
          label,
          description,
          colorId,
          projectModelIds,
        })(model);

        parents.forEach((parent) => {
          if (!existingClassIris.has(parent.value)) {
            addModelConceptClass({
              iri: parent.value,
              label: parent.text,
              projectModelIds,
            })(model);
          }
          addModelConceptHierarchy({ fromIri: iri, toIri: parent.value })(
            model
          );
        });

        children.forEach((child) => {
          if (!existingClassIris.has(child.value)) {
            addModelConceptClass({
              iri: child.value,
              label: child.text,
              projectModelIds,
            })(model);
          }
          addModelConceptHierarchy({ fromIri: child.value, toIri: iri })(model);
        });

        Object.values(addedRelationshipsByIri).forEach((relationship) => {
          addRelationshipItemToClass(iri, relationship)(model, diagram);
        });

        attributes.forEach((attribute) => {
          if (attribute.isNewlyCreated && !attribute.text) {
            return;
          }
          addAttributeItemToClass(iri, attribute)(model);
        });

        setGeoDataForIri(iri, geoData)(model);
      },
      { layout: DiagramModelCommitLayout.PARTIAL }
    );

    // since we don't want the partial relayout to happen
    // if we're ONLY changing the selection, we actually
    // need to put this separately
    commitDiagramWhileSkippingUndoManager(diagram, (_model, diagram) => {
      const classToSelect = diagram.findNodesByExample({
        value: iri,
        category: NodeCategory.MODEL_CONCEPT_CLASS,
      });

      // select the class that was just created
      if (classToSelect) {
        setDiagramSelection(new go.Set(classToSelect), true)(diagram);
      }
      rescaleDiagram(diagram, true);
    });

    handleCancel();
  };

  return (
    <ProjectDiagramCreateEntityDrawer
      disabled={Boolean(
        errorText.label || errorText.relationship || !label.trim()
      )}
      errorIntent={didEditLabel ? Intent.DANGER : Intent.NONE}
      errorText={errorText.label || errorText.relationship}
      isOpen={isOpen}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      title={copy.components.projectDiagram.classDrawer.createTitle}
    >
      <Tabs
        animate={false}
        onChange={(tabId: string) => setSelectedTabId(tabId as ClassDrawerTab)}
        renderActiveTabPanelOnly
        selectedTabId={selectedTabId}
      >
        <Tab
          data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.basic}
          id={ClassDrawerTab.BASIC}
          panel={
            <>
              <ProjectDiagramEditableIri
                errorIntent={didEditLabel ? Intent.DANGER : Intent.NONE}
                header={copy.components.projectDiagram.classDrawer.name}
                iri={iri}
                label={label}
                onSetIri={handleUpdateIri}
                onSetLabel={handleUpdateLabel}
                placeholder={
                  label === ''
                    ? copy.components.projectDiagram.modelConceptClass.new
                    : ''
                }
                rightEl={
                  <ColorPicker colorId={colorId} onSetColorId={setColorId} />
                }
                setValueOnChange
                testId={TEST_IDS.projectDiagram.entityDrawer.name}
              />
              <ProjectDiagramEditableTextArea
                header={copy.components.projectDiagram.entityDrawer.description}
                onSetValue={setDescription}
                value={description}
              />

              {allModelItems.length > 1 ? (
                <SelectableLinks
                  allowEmpty={false}
                  buttonText=""
                  filterTestId={
                    TEST_IDS.projectDiagram.entityDrawer.selectorFilter
                  }
                  header={copy.components.projectDiagram.entityDrawer.models}
                  items={availableModels}
                  itemTestId={(value, idx) =>
                    getProjectDiagramEntityDrawerSelectLinkTestId(
                      value,
                      'models',
                      idx
                    )
                  }
                  onRemove={handleRemoveModel}
                  onSelect={handleAddModel}
                  removeDisabledTooltip={
                    copy.components.projectDiagram.entityDrawer
                      .modelsRemoveDisabled
                  }
                  selected={models}
                  selectorItemTestId={
                    getProjectDiagramEntityDrawerSelectItemTestId
                  }
                  selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
                    'models'
                  )}
                />
              ) : null}
            </>
          }
          title={copy.components.projectDiagram.classDrawer.tabs.basic}
        />
        <Tab
          data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.hierarchy}
          id={ClassDrawerTab.HIERARCHY}
          panel={
            <div className="sd-drawer-project-diagram-entity-columns">
              <SelectableLinks
                buttonText=""
                creatable
                createNewItemFromQuery={createNewClassFromQuery}
                createNewItemRenderer={createNewSelectItemRenderer}
                createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
                  ''
                )}
                filterTestId={
                  TEST_IDS.projectDiagram.entityDrawer.selectorFilter
                }
                header={
                  copy.components.projectDiagram.classDrawer.parentClasses
                }
                items={hierarchySelectItems.availableParents}
                itemTestId={(value, idx) =>
                  getProjectDiagramEntityDrawerSelectLinkTestId(
                    value,
                    ModelConceptClassHierarchy.PARENTS,
                    idx
                  )
                }
                onRemove={handleRemoveParent}
                onSelect={handleAddParent}
                selected={parents}
                selectorItemTestId={
                  getProjectDiagramEntityDrawerSelectItemTestId
                }
                selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
                  ModelConceptClassHierarchy.PARENTS
                )}
              />
              <SelectableLinks
                buttonText=""
                creatable
                createNewItemFromQuery={createNewClassFromQuery}
                createNewItemRenderer={createNewSelectItemRenderer}
                createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
                  ''
                )}
                filterTestId={
                  TEST_IDS.projectDiagram.entityDrawer.selectorFilter
                }
                header={copy.components.projectDiagram.classDrawer.childClasses}
                items={hierarchySelectItems.availableChildren}
                itemTestId={(value, idx) =>
                  getProjectDiagramEntityDrawerSelectLinkTestId(
                    value,
                    ModelConceptClassHierarchy.CHILDREN,
                    idx
                  )
                }
                onRemove={handleRemoveChild}
                onSelect={handleAddChild}
                selected={children}
                selectorItemTestId={
                  getProjectDiagramEntityDrawerSelectItemTestId
                }
                selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
                  ModelConceptClassHierarchy.CHILDREN
                )}
              />
            </div>
          }
          title={copy.components.projectDiagram.classDrawer.tabs.hierarchy}
        />
        <Tab
          data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.relationships}
          id={ClassDrawerTab.RELATIONSHIPS}
          panel={
            <ProjectDiagramClassDrawerRelationships
              additionalIris={allNewIris}
              canUpdateDirectly
              onAddRelationship={handleAddRelationship}
              onUnlinkRelationship={handleUnlinkRelationship}
              onUpdateRelationship={handleUpdateRelationship}
              relationships={addedAndInheritedRelationships}
            />
          }
          title={copy.components.projectDiagram.classDrawer.tabs.relationships}
        />
        <Tab
          data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.attributes}
          id={ClassDrawerTab.ATTRIBUTES}
          panel={
            <ProjectDiagramAttributesTable
              additionalIris={allNewIris}
              attributes={attributes}
              onAddAttribute={handleAddAttribute}
              onUnlinkAttribute={handleRemoveAttribute}
            />
          }
          title={copy.components.projectDiagram.classDrawer.tabs.attributes}
        />
        <Tab
          data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.geospatial}
          id={ClassDrawerTab.GEOSPATIAL}
          panel={
            <ProjectDiagramClassDrawerGeospatialTab
              attributeItems={attributes}
              geoDataByIri={geoData}
              onUpdateGeoData={setGeoData}
            />
          }
          title={copy.components.projectDiagram.classDrawer.tabs.geospatial}
        />
      </Tabs>
    </ProjectDiagramCreateEntityDrawer>
  );
};
